/** Activity multipliers used to estimate total daily energy expenditure. */
export const ACTIVITY_LEVELS = Object.freeze([
  Object.freeze({ key: 'sedentary', label: 'Sedentary', multiplier: 1.2 }),
  Object.freeze({ key: 'light', label: 'Lightly active', multiplier: 1.3 }),
  Object.freeze({ key: 'moderate', label: 'Moderately active', multiplier: 1.45 }),
  Object.freeze({ key: 'active', label: 'Very active', multiplier: 1.6 }),
  Object.freeze({ key: 'athlete', label: 'Extremely active', multiplier: 1.75 }),
]);

export const DEFAULT_ACTIVITY_LEVEL = 'moderate';
const POUNDS_PER_KILOGRAM = 2.204_622_621_8;
const CENTIMETERS_PER_INCH = 2.54;
const MALE_BMR_OFFSET = 5;
const FEMALE_BMR_OFFSET = -161;
const DAILY_CALORIES_PER_WEEKLY_POUND = 500;

/**
 * The lowest daily target this app will recommend, whatever the chosen pace.
 *
 * Exported because the floor is invisible otherwise: when it engages, the target stops
 * responding to the pace and to switching between Weight loss and Maintain, which reads
 * as a broken calculation unless the UI can say the floor is what is holding it.
 */
export const MINIMUM_CALORIE_TARGET = 1200;
const FAT_CALORIE_FRACTION = 0.25;

/**
 * Estimate BMR and TDEE from a complete profile using Mifflin-St Jeor.
 *
 * @param {{weightLb: number, heightIn: number, age: number, sex: 'female'|'male', activity: string}} profile
 * @returns {{bmr: number, tdee: number}}
 */
export function calculateEnergyNeeds(profile) {
  const kilograms = profile.weightLb / POUNDS_PER_KILOGRAM;
  const centimeters = profile.heightIn * CENTIMETERS_PER_INCH;
  const offset = profile.sex === 'female' ? FEMALE_BMR_OFFSET : MALE_BMR_OFFSET;
  const bmr = Math.round(10 * kilograms + 6.25 * centimeters - 5 * profile.age + offset);
  const activity = ACTIVITY_LEVELS.find((item) => item.key === profile.activity);
  const multiplier = activity?.multiplier ?? activityMultiplier(DEFAULT_ACTIVITY_LEVEL);
  return { bmr, tdee: Math.round(bmr * multiplier) };
}

/**
 * Resolve a documented activity multiplier.
 *
 * @param {string} key
 * @returns {number}
 */
export function activityMultiplier(key) {
  return ACTIVITY_LEVELS.find((item) => item.key === key)?.multiplier ?? 1.45;
}

/**
 * How many calories lower a rest day's target sits than a training day's.
 *
 * The activity multiplier is a weekly average: "moderately active" already assumes several
 * training days in the week, so it over-feeds the days with no training in them. Rather
 * than re-model the week, a rest day steps one rung down the same documented ladder and
 * hands back the difference, `BMR × (this multiplier − the one below it)`. Sedentary has
 * nothing below it because it already assumes no training, so it correctly drops nothing.
 *
 * Deliberately anchored to BMR rather than to maintenance: a manually set maintenance
 * figure is the user's own correction to their whole week, and scaling the rest-day drop by
 * it would apply that correction twice.
 *
 * @param {{bmr: number, activity: string}} input
 * @returns {number}
 */
export function restDayCalorieDrop({ bmr, activity }) {
  const found = ACTIVITY_LEVELS.findIndex((item) => item.key === activity);
  const index = found < 0 ? ACTIVITY_LEVELS.findIndex((item) => item.key === DEFAULT_ACTIVITY_LEVEL) : found;
  const lower = ACTIVITY_LEVELS[Math.max(0, index - 1)].multiplier;
  return Math.max(0, Math.round(bmr * (ACTIVITY_LEVELS[index].multiplier - lower)));
}

/** Split a calorie target into fat and carbs around a protein figure that never moves. */
function macrosFor(targetCalories, proteinG) {
  const fatG = Math.round((targetCalories * FAT_CALORIE_FRACTION) / 9);
  return { fatG, carbG: Math.max(0, Math.round((targetCalories - proteinG * 4 - fatG * 9) / 4)) };
}

/**
 * Calculate the one calorie and macro plan used by the API, UI and reminders.
 *
 * Two targets come out of this, not one: the training-day figure this app has always
 * produced, and a lower rest-day figure for dates with no workout and no session on them.
 * Which of the two applies to a given date is a calendar question, answered by
 * `dayCalorieTarget` in ./rest-day.js rather than here.
 *
 * @param {object} profile
 * @returns {{bmr:number,tdee:number,targetCalories:number,proteinG:number,carbG:number,fatG:number}}
 */
export function calculateNutritionPlan(profile) {
  const { bmr, tdee } = calculateEnergyNeeds(profile);
  const maintenanceCalories = profile.maintenanceCalories ?? tdee;
  const direction = profile.goal === 'cut' ? -1 : profile.goal === 'gain' ? 1 : 0;
  const paced = Math.round(maintenanceCalories + direction * profile.rateLbPerWeek * DAILY_CALORIES_PER_WEEKLY_POUND);
  const calculated = Math.max(MINIMUM_CALORIE_TARGET, paced);
  const usingDailyGoal = profile.dailyGoal > 0;
  const targetCalories = usingDailyGoal ? profile.dailyGoal : calculated;
  // Whether the floor, rather than the chosen pace, is what set the target. Reported so
  // the UI can explain a target that has stopped responding to the pace instead of
  // leaving it looking stuck. A manual daily goal bypasses the floor, so it is never
  // floored in that case.
  const targetFloored = !usingDailyGoal && paced < MINIMUM_CALORIE_TARGET;
  const proteinG = Math.round(profile.proteinPerLb * profile.weightLb);
  const { fatG, carbG } = macrosFor(targetCalories, proteinG);
  // The rest-day figure hangs off whatever the training day landed on, so a manual daily
  // goal carries into it too rather than being quietly ignored on three days a week.
  const restDayCustom = profile.restDayCalories > 0;
  const restPaced = targetCalories - restDayCalorieDrop({ bmr, activity: profile.activity });
  const restDayTargetCalories = restDayCustom ? profile.restDayCalories : Math.max(MINIMUM_CALORIE_TARGET, restPaced);
  // The same floor guards rest days, so a target already held at the minimum has nothing
  // left to give and both days read alike. Reported for the same reason as `targetFloored`.
  const restDayFloored = !restDayCustom && restPaced < MINIMUM_CALORIE_TARGET;
  const restMacros = macrosFor(restDayTargetCalories, proteinG);
  return { bmr, tdee, maintenanceCalories, targetCalories, targetFloored, proteinG, carbG, fatG,
    restDayTargetCalories, restDayFloored, restDayCustom,
    // Signed on purpose: positive is the usual case of eating less, but a manual rest-day
    // figure set above the training-day target is legitimate and should not read as a drop.
    restDayDrop: targetCalories - restDayTargetCalories,
    restDayProteinG: proteinG, restDayCarbG: restMacros.carbG, restDayFatG: restMacros.fatG };
}
